import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, ChevronsUpDown, RefreshCw } from "lucide-react";
import { X } from "lucide-react";
import { supplierApi, type KeyStatus, type SupplierKeyUsage, type SupplierTargetUsage } from "@/api/supplierClient";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/Pagination";
import { useTranslation } from "@/framework-originated/i18n";

// reconcileQ 对账卡全量取数:后端 page_size 上限 1000(doc/054 §2.1)。
const RECONCILE_PAGE_SIZE = 1000;

type UsageSortKey = "" | "key_name" | "total" | "created_at";
type UsageSortDir = "" | "asc" | "desc";
type UsageCoverage = "" | "complete" | "partial" | "none";

const TIME_WINDOWS = [
  { key: "all", ms: 0 },
  { key: "1h", ms: 3600_000 },
  { key: "24h", ms: 86400_000 },
  { key: "7d", ms: 7 * 86400_000 },
];

function timeWindowLabel(t: (key: string) => string, win: string): string {
  switch (win) {
    case "1h":
      return t("usage.time1h");
    case "24h":
      return t("usage.time24h");
    case "7d":
      return t("usage.time7d");
    default:
      return t("usage.timeAll");
  }
}

function buildStatusLabel(t: (key: string) => string): Record<KeyStatus, string> {
  return {
    active: t("usage.statusActive"),
    inactive: t("usage.statusInactive"),
    expired: t("usage.statusExpired"),
  };
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function fmtTotal(usage: SupplierKeyUsage | undefined): string {
  if (!usage || usage.total == null) return "—";
  return `${usage.total.toFixed(4)} ${usage.display_unit || ""}`.trim();
}

function fmtTargetTotal(target: SupplierTargetUsage): string {
  if (target.total == null) return "—";
  return target.total.toFixed(4);
}

function SupplierUsageCell({
  usage,
  refreshing,
  onRefresh,
}: {
  usage: SupplierKeyUsage | undefined;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [position, setPosition] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    maxHeight: number;
  } | null>(null);
  const tooltipId = usage ? `supplier-usage-detail-${usage.key_id}` : undefined;
  const busy = refreshing;
  const valueLabel = usage ? fmtTotal(usage) : "—";
  const secondary =
    !usage ? "暂无数据"
    : usage.latest_sampled_at ? `采样 ${usage.latest_sampled_at.slice(0, 10)}`
    : usage.total == null ? "暂无数据"
    : "";

  const open = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const anchor = anchorRef.current;
    const summary = usage;
    if (!anchor || !summary) return;
    const rect = anchor.getBoundingClientRect();
    const viewportGap = 8;
    const anchorGap = 4;
    const tooltipMaxHeight = 400;
    const width = Math.min(320, window.innerWidth - viewportGap * 2);
    const availableBelow = window.innerHeight - rect.bottom - anchorGap - viewportGap;
    const availableAbove = rect.top - anchorGap - viewportGap;
    const openAbove = availableBelow < tooltipMaxHeight && availableAbove > availableBelow;
    const availableHeight = openAbove ? availableAbove : availableBelow;
    setPosition({
      left: Math.max(viewportGap, Math.min(rect.left, window.innerWidth - width - viewportGap)),
      maxHeight: Math.max(0, Math.min(tooltipMaxHeight, availableHeight)),
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + anchorGap }
        : { top: rect.bottom + anchorGap }),
    });
  };

  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPosition(null), 120);
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        ref={anchorRef}
        type="button"
        className="rounded px-1 py-0.5 text-left font-mono text-xs outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
        onMouseEnter={open}
        onMouseLeave={closeSoon}
        onFocus={open}
        onBlur={closeSoon}
        aria-describedby={tooltipId}
        aria-expanded={position != null}
      >
        <span className={usage?.total != null ? "font-semibold text-foreground" : "text-muted-foreground"}>{valueLabel}</span>
        {secondary && (
          <span className="ml-1.5 font-sans text-[11px] text-muted-foreground">{secondary}</span>
        )}
      </button>
      <button
        type="button"
        className="rounded p-1 text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
        onClick={onRefresh}
        disabled={busy}
        title="只刷新该 Key 用量"
        aria-label="只刷新该 Key 用量"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
      </button>
      {position && usage && createPortal(
        <div
          id={tooltipId}
          role="tooltip"
          className="fixed z-[100] flex w-80 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg border bg-card p-2.5 text-card-foreground shadow-2xl"
          style={{ left: position.left, top: position.top, bottom: position.bottom, maxHeight: position.maxHeight }}
          onMouseEnter={cancelClose}
          onMouseLeave={closeSoon}
        >
          <div className="mb-2 flex shrink-0 items-center justify-between border-b pb-2">
            <div>
              <div className="text-sm font-semibold">供应商用量明细</div>
              <div className="text-[11px] text-muted-foreground">
                {usage.targets.length}/{usage.targets.length} 个分发目标{usage.display_unit ? ` · 单位 ${usage.display_unit}` : ""}
              </div>
            </div>
            <Badge variant={usage.coverage === "complete" ? "success" : usage.coverage === "partial" ? "warning" : "muted"}>
              {usage.coverage === "complete" ? "完整" : usage.coverage === "partial" ? "部分数据" : "暂无数据"}
            </Badge>
          </div>
          {usage.targets.length === 0 ? (
            <div className="min-h-0 overflow-y-auto py-2 text-xs text-muted-foreground">该 Key 尚未分发至任何目标站点。</div>
          ) : (
            <div className="min-h-0 space-y-2 overflow-y-auto pr-1">
              {usage.targets.map((target) => (
                <div
                  key={`${target.site_type}:${target.target_code}`}
                  className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 rounded-md border bg-secondary px-2.5 py-2 text-xs shadow-sm"
                >
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate font-medium">{target.target_code}</span>
                      <Badge variant="muted" className="shrink-0 border border-border px-1.5 py-0 text-[10px] font-normal">
                        {target.site_type === "relay" ? "中继站" : target.site_type === "push" ? "推送站" : target.site_type}
                      </Badge>
                    </div>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {target.last_success_at
                        ? `采集于 ${new Date(target.last_success_at).toLocaleString("zh-CN", { hour12: false })}`
                        : "从未成功采集"}
                      <span className={target.status === "success" ? "ml-1.5 text-green-600" : target.status === "failed" ? "ml-1.5 text-destructive" : "ml-1.5 text-amber-600"}>
                        · {target.status === "success" ? "成功" : target.status === "failed" ? "失败" : target.status}
                      </span>
                    </div>
                  </div>
                  <div className="self-center whitespace-nowrap font-mono font-semibold">
                    {fmtTargetTotal(target)}
                    <span className="ml-1 font-sans text-[10px] font-normal text-muted-foreground">
                      {target.coverage === "partial" ? "部分" : ""}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// /supplier/usage — Key(只读)+ 用量对账(doc/052 §2.4):按 Key 汇总 + 按目标明细
// + 合计/最近采样,供供应商对账。新增 Key 由「分发」页承接(doc/051);本页不改变
// 任何数据。归属隔离在服务层强制 WHERE owner_supplier_id=me,用量响应只含脱敏
// target_code(参考运营 /keys 与供应商旧 /supplier/keys)。
export function SupplierUsagePage() {
  const { t } = useTranslation("supplier");
  const STATUS_LABEL = buildStatusLabel(t);
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"" | KeyStatus>("");
  const [coverage, setCoverage] = useState<UsageCoverage>("");
  const [timeWin, setTimeWin] = useState("all");
  const [sortBy, setSortBy] = useState<UsageSortKey>("");
  const [sortDir, setSortDir] = useState<UsageSortDir>("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const createdAfter = useMemo(() => {
    const win = TIME_WINDOWS.find((w) => w.key === timeWin)?.ms ?? 0;
    return win > 0 ? new Date(Date.now() - win).toISOString() : undefined;
  }, [timeWin]);

  // 拆两条查询(doc/054 §2.2):
  //  - reconcileQ:无过滤全量(大 page_size)→ 对账卡,不被表格筛选牵着走。
  //  - tableQ:分页/排序/过滤 → 表格行;行内直接带 usage,不再做 usageByKey 合并。
  const reconcileQ = useQuery({
    queryKey: ["supplier", "usage", "reconcile"],
    queryFn: () => supplierApi.usageSummaries({ page_size: RECONCILE_PAGE_SIZE }),
  });
  const tableQ = useQuery({
    queryKey: ["supplier", "usage", "table", { page, pageSize, q, status, coverage, timeWin, sortBy, sortDir }],
    queryFn: () =>
      supplierApi.usageSummaries({
        page,
        page_size: pageSize,
        ...(sortBy ? { sort: sortBy } : {}),
        ...(sortDir ? { order: sortDir } : {}),
        ...(q ? { q } : {}),
        ...(status ? { status } : {}),
        ...(coverage ? { coverage } : {}),
        ...(createdAfter ? { created_after: createdAfter } : {}),
      }),
  });

  const refreshMut = useMutation({
    mutationFn: (ids: number[]) => supplierApi.refreshUsage(ids),
    onSuccess: () => {
      setErr(null);
      setMsg(t("usage.usageRefreshSuccess"));
      // 前缀匹配同时命中 reconcileQ 与 tableQ。
      qc.invalidateQueries({ queryKey: ["supplier", "usage"] });
    },
    onError: (e) => {
      setMsg(null);
      setErr(e instanceof ApiError ? e.message : t("usage.usageRefreshFailed"));
    },
  });

  const items = tableQ.data?.items ?? [];
  const total = tableQ.data?.total ?? 0;
  const usageItems = reconcileQ.data?.items ?? [];

  // 对账汇总(doc/052 §2.4):合计用量、覆盖 Key 数、最近采样时间。
  const reconcile = useMemo(() => {
    let sum = 0;
    let unit = "";
    let latest: string | null = null;
    for (const u of usageItems) {
      if (u.total != null) sum += u.total;
      if (!unit && u.display_unit) unit = u.display_unit;
      if (u.latest_sampled_at && (!latest || u.latest_sampled_at > latest)) latest = u.latest_sampled_at;
    }
    return { sum, unit, latest };
  }, [usageItems]);

  // 三态排序:asc → desc → none(复刻 TableShell cycleSort 交互)。
  const cycleSort = (key: UsageSortKey) => {
    setPage(1);
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortBy("");
      setSortDir("");
    }
  };

  const sortHeader = (label: string, sortKey: UsageSortKey) => (
    <th className="px-4 py-2 font-medium">
      <button
        type="button"
        onClick={() => cycleSort(sortKey)}
        aria-label={`${label}${sortBy === sortKey ? (sortDir === "asc" ? t("usage.sortAsc") : t("usage.sortDesc")) : ""}`}
        className="inline-flex items-center gap-1 rounded transition-colors hover:text-foreground"
      >
        {label}
        {sortBy !== sortKey ? (
          <ChevronsUpDown className="h-3 w-3 opacity-50" />
        ) : sortDir === "asc" ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
      </button>
    </th>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Supplier / usage</p>
          <h1 className="text-xl font-semibold">{t("usage.title")}</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("usage.description")}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          disabled={refreshMut.isPending || items.length === 0}
          onClick={() => refreshMut.mutate(items.map((u) => u.key_id))}
        >
          <RefreshCw className={`mr-1 h-4 w-4 ${refreshMut.isPending ? "animate-spin" : ""}`} />
          {refreshMut.isPending ? t("usage.refreshing") : t("usage.refreshUsage")}
        </Button>
      </div>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border bg-card px-4 py-3 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("usage.reconcileTotal")}</p>
          <p className="mt-1 font-mono text-lg font-semibold">
            {usageItems.length === 0 || (reconcile.sum === 0 && !reconcile.unit)
              ? "—"
              : `${reconcile.sum.toFixed(4)} ${reconcile.unit}`.trim()}
          </p>
        </div>
        <div className="rounded-xl border bg-card px-4 py-3 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("usage.reconcileKeys")}</p>
          <p className="mt-1 font-mono text-lg font-semibold">{usageItems.length}</p>
        </div>
        <div className="rounded-xl border bg-card px-4 py-3 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("usage.reconcileSampled")}</p>
          <p className="mt-1 font-mono text-sm font-semibold">{reconcile.latest ? fmtTime(reconcile.latest) : "—"}</p>
        </div>
      </section>

      {(msg || err) && (
        <div
          className={`rounded-md border px-3 py-2 text-sm ${
            err ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-green-200 bg-green-50 text-green-800"
          }`}
        >
          {err ?? msg}
        </div>
      )}

      <section className="rounded-xl border bg-card shadow-sm">
        <header className="flex flex-wrap items-center gap-3 border-b px-5 py-3">
          <h2 className="text-sm font-semibold">{t("usage.listTitle", { count: total })}</h2>
          <div className="ml-auto flex items-center gap-2">
            <Input
              className="h-8 w-48"
              placeholder={t("usage.search")}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <select
              className="h-8 rounded-md border bg-background px-2 text-sm outline-none"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as "" | KeyStatus);
                setPage(1);
              }}
            >
              <option value="">{t("usage.status")}</option>
              <option value="active">{t("usage.statusActive")}</option>
              <option value="inactive">{t("usage.statusInactive")}</option>
              <option value="expired">{t("usage.statusExpired")}</option>
            </select>
            <select
              className="h-8 rounded-md border bg-background px-2 text-sm outline-none"
              value={coverage}
              onChange={(e) => {
                setCoverage(e.target.value as UsageCoverage);
                setPage(1);
              }}
            >
              <option value="">{t("usage.coverageAll")}</option>
              <option value="complete">{t("usage.coverageComplete")}</option>
              <option value="partial">{t("usage.coveragePartial")}</option>
              <option value="none">{t("usage.coverageNone")}</option>
            </select>
            <span className="mx-1 text-xs text-muted-foreground">|</span>
            {TIME_WINDOWS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => {
                  setTimeWin(w.key);
                  setPage(1);
                }}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                  timeWin === w.key ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
                }`}
              >
                {timeWindowLabel(t, w.key)}
              </button>
            ))}
          </div>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-left text-xs text-muted-foreground">
                {sortHeader(t("usage.name"), "key_name")}
                <th className="px-4 py-2 font-medium">{t("usage.keyHint")}</th>
                <th className="px-4 py-2 font-medium">{t("usage.models")}</th>
                <th className="px-4 py-2 font-medium">{t("usage.status")}</th>
                {sortHeader(t("usage.usageTotal"), "total")}
                {sortHeader(t("usage.createdAt"), "created_at")}
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.key_id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-2 font-medium">{u.key_name || `#${u.key_id}`}</td>
                  <td className="px-4 py-2 font-mono text-xs">{u.key_hint || "—"}</td>
                  <td className="px-4 py-2 text-xs">{u.models || "—"}</td>
                  <td className="px-4 py-2">
                    <Badge variant={u.status === "active" ? "success" : u.status === "expired" ? "muted" : "warning"}>
                      {STATUS_LABEL[u.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    <SupplierUsageCell
                      usage={u}
                      refreshing={refreshMut.isPending}
                      onRefresh={() => refreshMut.mutate([u.key_id])}
                    />
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{fmtTime(u.created_at)}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    {tableQ.isLoading ? t("usage.loading") : t("usage.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination className="px-5 pb-3" page={page} total={tableQ.data?.total} pageSize={pageSize}
          onPageChange={setPage} onPageSizeChange={setPageSize} loading={tableQ.isFetching} />
      </section>

      <section className="rounded-xl border bg-card shadow-sm">
        <header className="border-b px-5 py-3">
          <h2 className="text-sm font-semibold">{t("usage.usageTargets")}</h2>
        </header>
        <div className="divide-y">
          {usageItems.map((usage) => (
            <div key={usage.key_id} className="px-5 py-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">{usage.key_name || `#${usage.key_id}`}</span>
                <span className="ml-auto font-mono text-xs text-muted-foreground">{fmtTotal(usage)}</span>
              </div>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                {usage.targets.map((target) => (
                  <li
                    key={`${usage.key_id}-${target.target_code}-${target.site_type}`}
                    className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2.5 py-1 text-[11px]"
                  >
                    <span className="font-medium">{target.target_code}</span>
                    <span className="text-muted-foreground">
                      {target.total != null ? target.total.toFixed(4) : "—"}
                    </span>
                  </li>
                ))}
                {usage.targets.length === 0 && <li className="text-[11px] text-muted-foreground">—</li>}
              </ul>
            </div>
          ))}
          {usageItems.length === 0 && (
            <p className="px-5 py-4 text-sm text-muted-foreground">
              {reconcileQ.isLoading ? t("usage.loading") : t("usage.usageEmpty")}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
